/**
 * @fileoverview Login form component that handles user authentication
 * @module components/auth/LoginForm
 */

import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { FormEvent, useState, useCallback } from "react";
import { useToast } from "../ui/use-toast";
import { useLanguage } from "../../contexts/LanguageContext";
import { Mail, Lock, Eye, EyeOff, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { useSupportEmail } from "../../hooks/useSupportEmail";

// Regular expression to validate email addresses
const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

// Interface for the form's state
interface FormState {
  email: string;
  password: string;
  showPassword: boolean;
  rememberMe: boolean;
}

// Interface for form validation errors
interface FormErrors {
  email?: string;
  password?: string;
}

/**
 * LoginForm Component
 * @description Handles user authentication through a form interface
 */
export const LoginForm = () => {
  // Hooks and State
  const { login, resetPassword, setPersistenceType } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { supportEmail } = useSupportEmail();

  const [formData, setFormData] = useState<FormState>({
    email: "",
    password: "",
    showPassword: false,
    rememberMe: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});

  // Form Validation
  const validateField = useCallback(
    (name: string, value: string) => {
      switch (name) {
        case "email":
          if (!value) {
            return t("auth.login.email.required");
          }
          if (!EMAIL_REGEX.test(value)) {
            return t("auth.login.email.invalid");
          }
          return undefined;
        case "password":
          if (!value) {
            return t("auth.login.password.required");
          }
          return undefined;
        default:
          return undefined;
      }
    },
    [t]
  );

  const validateForm = useCallback(() => {
    const newErrors = {
      email: validateField("email", formData.email),
      password: validateField("password", formData.password),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== undefined);
  }, [formData, validateField]);

  // Event Handlers
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched({ email: true, password: true });

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await setPersistenceType(formData.rememberMe);
      const success = await login({
        email: formData.email,
        password: formData.password,
        rememberMe: formData.rememberMe,
      });

      if (success) {
        toast({
          variant: "default",
          title: t("auth.toasts.loginSuccess.title"),
          description: t("auth.toasts.loginSuccess.description"),
        });
        setTimeout(() => {
          navigate("/");
        }, 500);
      }
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: t("auth.toasts.loginFailed.title"),
        description: t("auth.toasts.loginFailed.description"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!formData.email) {
      toast({
        variant: "destructive",
        title: t("auth.toasts.emailRequired.title"),
        description: t("auth.toasts.emailRequired.description"),
      });
      return;
    }

    try {
      await resetPassword(formData.email);
      toast({
        variant: "default",
        title: t("auth.toasts.resetSuccess.title"),
        description: t("auth.toasts.resetSuccess.description"),
      });
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: t("auth.toasts.resetFailed.title"),
        description: t("auth.toasts.resetFailed.description"),
      });
    }
  };


  return (
    <div className="w-full">
      <div className="mb-8">
        <h2 className="text-2xl font-semibold mb-2">{t("auth.login.title")}</h2>
        <p className="text-muted-foreground">{t("auth.login.subtitle")}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-5">
          {/* Email Field */}
          <div>
            <Label htmlFor="email" className="mb-2.5 block font-normal">
              {t("auth.login.email.label")}
              <span className="text-destructive ml-0.5">*</span>
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={t("auth.login.email.placeholder")}
              className={`${errors.email ? "border-red-500" : ""} placeholder:text-gray-300`}
              icon={<Mail className="w-5 h-5" />}
            />
            {errors.email && touched.email && (
              <div className="flex gap-x-2 items-center mt-2">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                <p className="text-sm text-red-500">{errors.email}</p>
              </div>
            )}
          </div>

          {/* Password Field */}
          <div>
            <div className="mb-2.5">
              <Label htmlFor="password" className="font-normal inline-flex items-center">
                {t("auth.login.password.label")}
                <span className="text-destructive ml-0.5">*</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="ml-1 text-[#169C9D] focus:outline-none">
                        <Info className="w-4 h-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="right"
                      align="center"
                      sideOffset={5}
                      className="bg-[#169C9D] text-white rounded-lg px-4 py-2.5"
                    >
                      <p>{t("auth.login.password.tooltip")}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Label>
            </div>
            <Input
              id="password"
              name="password"
              type={formData.showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={t("auth.login.password.placeholder")}
              className={`${errors.password ? "border-red-500" : ""} placeholder:text-gray-300`}
              icon={<Lock className="w-5 h-5" />}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, showPassword: !prev.showPassword }))}
                  className="focus:outline-none"
                >
                  {formData.showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              }
            />
            {errors.password && touched.password && (
              <div className="flex gap-x-2 items-center mt-2">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                <p className="text-sm text-red-500">{errors.password}</p>
              </div>
            )}
          </div>
        </div>

        {/* Forgot Password Link */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">{t("auth.login.forgotPassword.text")}</span>
          <Button
            variant="link"
            type="button"
            onClick={handleResetPassword}
            className="px-0 h-auto font-normal text-[#169C9D]"
          >
            {t("auth.login.forgotPassword.button")}
          </Button>
        </div>

        {/* Remember Me Checkbox */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="rememberMe"
            checked={formData.rememberMe}
            onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, rememberMe: checked === true }))}
          />
          <Label htmlFor="rememberMe" className="font-normal text-gray-600">
            {t("auth.login.rememberMe")}
          </Label>
        </div>
        

        {/* Participation Link */}
        <div className="text-sm text-center text-gray-600">
          <span>{t("auth.participation.joinText")} </span>
          <Link to="/participation" className="text-[#169C9D] hover:underline">
            {t("auth.participation.joinLink")}
          </Link>
        </div>

        
        {/* Submit Button */}
        <Button
          type="submit"
          className="w-full bg-[#169C9D] hover:bg-[#138889] text-white rounded-md h-10"
          disabled={isSubmitting}
        >
          {isSubmitting ? t("auth.login.loginButton.loading") : t("auth.login.loginButton.default")}
        </Button>

        {/* Help Link */}
        <div className="text-sm text-center text-gray-600">
            <span>{t("auth.login.needHelp.text")} </span>
            <a href={`mailto:${supportEmail}`} className="text-[#169C9D] hover:underline">
              {t("auth.login.needHelp.link")}
            </a>
        </div>
      </form>
    </div>
  );
};
